import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/api";
import { Reveal, Overline } from "@/components/Motion";
import Lightbox from "@/components/Lightbox";
import { Figure, normalizeImages } from "@/components/ProjectImages";

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === false) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm">Project not found.</p></main>;
  if (!p) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm text-ink/40">Loading…</p></main>;

  const galleryImgs = normalizeImages(p.gallery);
  const drawingImgs = normalizeImages(p.drawings);
  const allImages = [{ url: p.cover, caption: "", alt: p.title }, ...galleryImgs, ...drawingImgs];
  const openViewer = (i) => setViewer(i);

  const meta = [
    ["Location", p.location], ["Year", p.year], ["Status", p.status],
    ["Type", p.type], ["Area", p.area], ["Category", p.category],
  ];

  return (
    <main data-testid="project-detail" className="pt-28 pb-24">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Link to="/work/architecture" data-testid="back-to-architecture" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60">
          <ArrowLeft size={14} /> All Architecture
        </Link>
        <Reveal className="mt-8">
          <Overline>{p.category}</Overline>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl tracking-tight mt-4 leading-[1.02]">{p.title}</h1>
        </Reveal>
      </div>
      <Reveal className="mt-12 px-6 md:px-12 max-w-[1600px] mx-auto">
        <Figure
          image={allImages[0]}
          fallbackCaption={p.title}
          onOpen={() => openViewer(0)}
          aspectClass="aspect-[16/8]"
          imgClassName="w-full h-full object-cover"
          eager
          testid="cover-figure"
        />
      </Reveal>
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Reveal className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-6 border-y border-ink/15 py-8" >
          {meta.map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">{k}</p>
              <p className="font-display text-lg mt-2">{v}</p>
            </div>
          ))}
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16">
          <Reveal className="lg:col-span-7">
            <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-5">Concept</h2>
            <p className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{p.concept}</p>
            <p className="mt-8 text-ink/60 leading-relaxed max-w-xl">{p.description}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-5">Materials</h2>
              <ul className="space-y-2">
                {(p.materials || []).map((m) => (
                  <li key={m} className="flex items-center gap-3 text-sm text-ink/70 border-b border-ink/10 pb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-linen border border-ink/10 p-6">
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-4">Sustainability</h2>
              <p className="text-sm text-ink/70 leading-relaxed">{p.sustainability}</p>
            </div>
          </Reveal>
        </div>
        {!!drawingImgs.length && (
          <div className="mt-20">
            <Overline>Drawings & Sketches</Overline>
            <div className="mt-8 flex gap-6 overflow-x-auto pb-4 snap-x" data-testid="drawings-strip">
              {drawingImgs.map((d, i) => (
                <Figure
                  key={i}
                  image={d}
                  fallbackCaption={`${p.title} — drawing ${i + 1}`}
                  onOpen={() => openViewer(1 + galleryImgs.length + i)}
                  className="shrink-0 w-[80vw] sm:w-[420px] bg-white border border-ink/15 p-3 snap-start"
                  imgClassName="w-full h-auto"
                  testid={`drawing-figure-${i}`}
                />
              ))}
            </div>
          </div>
        )}
        <div className="mt-20">
          <Overline>Gallery</Overline>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {galleryImgs.map((g, i) => (
              <Reveal key={i} delay={i * 0.06} className={i === 0 ? "md:col-span-2" : ""}>
                <Figure
                  image={g}
                  fallbackCaption={`${p.title} — photograph ${i + 1}`}
                  onOpen={() => openViewer(1 + i)}
                  aspectClass={i === 0 ? "aspect-[16/8]" : "aspect-[4/3]"}
                  imgClassName="w-full h-full object-cover"
                  testid={`gallery-figure-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="mt-20 border-t border-ink/15 pt-10 flex flex-wrap items-center justify-between gap-6">
          <p className="font-display text-2xl tracking-tight">Interested in a project like this?</p>
          <Link to="/contact?type=Architecture" data-testid="project-enquire-btn"
            className="inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors">
            Start a Project
          </Link>
        </Reveal>
      </div>
      {viewer !== null && (
        <Lightbox images={allImages} index={viewer} onClose={() => setViewer(null)} onNavigate={setViewer} />
      )}
    </main>
  );
}
